import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import { FormGroup, MenuItem, Select, Text, withId } from '@theneatcompany/nui'

import FieldError from './FieldError'
import withField from './withField'

class SelectField extends Component {
  dropdownButtonRef = React.createRef()

  getCurrentItem = () => {
    const { options, field } = this.props
    return options.find(item => field.value === item.value) || null
  }

  getValue = (item) => {
    if (!item) return null
    const { renderLabel } = this.props
    const { label } = item

    return renderLabel?.(item) || label
  }

  filterItem = (query, item) => `${item.label.toLowerCase()}`.indexOf(query.toLowerCase()) >= 0

  handleItemSelect = (item) => {
    const { onSelect, field: { name }, form: { setValue } } = this.props
    if (!item) return

    setValue(name, item.value, { shouldValidate: true })
    if (onSelect) onSelect(item.value)
  }

  renderItemLabel = (label, value) => {
    const { renderItemLabel } = this.props
    return renderItemLabel ? renderItemLabel(label, value) : label
  }

  renderItem = (item, meta) => {
    const { hasSubElements } = this.props
    const currentItem = this.getCurrentItem()

    if (item?.isDivider) {
      return (
        <li key={item.value}>
          <Text size="small" typeStyle="bold">{item.label}</Text>
        </li>
      )
    }

    return (
      <MenuItem
        key={item.value}
        active={meta.modifiers?.active}
        onClick={meta.handleClick}
        selected={currentItem?.value === item.value}
        text={this.renderItemLabel(hasSubElements ? String('-- ').concat(item.label) : item.label, item.value)}
      />
    )
  }

  render() {
    const {
      errorProps,
      field,
      disabled,
      intl,
      labelProps,
      buttonProps,
      label = labelProps.label,
      placeholder = label,
      meta: { error, touched, invalid, isDirty },
      options,
      size,
      containerProps,
      hasSubElements,
      form: { formState: { isSubmitting, isSubmitted } },
      indicateValid,
      id,
      ...rest
    } = this.props
    const currentItem = this.getCurrentItem()

    const value = this.getValue(currentItem)
    const errorMessage = error?.message

    // @TODO figure out why touched is not being updated properly
    const hasErrors = isSubmitted || errorProps.ignoreTouched ? !!errorMessage : !!(errorMessage && touched)

    return (
      <FormGroup
        intent={hasErrors ? 'danger' : (indicateValid && (!invalid && isDirty) && 'success') || undefined}
        {...containerProps}
      >
        <FormGroup.Label htmlFor={id} label={label} {...labelProps} />
        <Select
          disabled={disabled || isSubmitting}
          id={id}
          itemPredicate={this.filterItem}
          itemRenderer={this.renderItem}
          items={options}
          onItemSelect={this.handleItemSelect}
          targetRef={this.dropdownButtonRef}
          {...field}
          {...rest}
        >
          {({ isOpen }) => (
            <Select.DropdownButton
              ref={this.dropdownButtonRef}
              active={isOpen}
              disabled={disabled || isSubmitting}
              fill
              size={size}
              {...buttonProps}
            >
              {value || placeholder}
            </Select.DropdownButton>
          )}
        </Select>
        <FieldError label={label} message={errorMessage} touched={touched} {...errorProps} />
      </FormGroup>
    )
  }
}

SelectField.defaultProps = {
  filterable: true,
  labelProps: {},
  disabled: false,
  onSelect: null,
  renderLabel: null,
  label: undefined,
  placeholder: undefined,
  errorProps: {},
  inputProps: {},
  buttonProps: {},
  size: 'medium',
  containerClassName: undefined,
  hasSubElements: false,
  renderItemLabel: null,
  containerProps: {},
  indicateValid: false,
}

SelectField.propTypes = {
  filterable: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  inputProps: PropTypes.shape({}),
  buttonProps: PropTypes.shape({}),
  meta: PropTypes.shape({
    error: PropTypes.shape({
      message: PropTypes.string,
    }),
    touched: PropTypes.bool,
    invalid: PropTypes.bool,
    isDirty: PropTypes.bool,
  }).isRequired,
  errorProps: PropTypes.shape({
    onRender: PropTypes.func,
    ignoreTouched: PropTypes.bool,
  }),
  label: PropTypes.string,
  labelProps: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line
  })).isRequired,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  renderLabel: PropTypes.func,
  size: PropTypes.string,
  containerClassName: PropTypes.string,
  hasSubElements: PropTypes.bool,
  name: PropTypes.string.isRequired,
  form: PropTypes.shape({
    setValue: PropTypes.func.isRequired,
    formState: PropTypes.shape({
      isSubmitted: PropTypes.bool,
      isSubmitting: PropTypes.bool,
    }),
  }).isRequired,
  renderItemLabel: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  field: PropTypes.shape({
    name: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    value: PropTypes.any,
  }).isRequired,
  containerProps: PropTypes.shape({}),
  indicateValid: PropTypes.bool,
}

export { SelectField }
export default injectIntl(withField(withId(props => props.name)(SelectField)))
